import React from 'react';
import { changePassword, SaveApiResMessage } from '@/actions';
import { noScroll, removeNoScroll } from '@/actions/Helper';
import { Input } from '@/components/Input';
import IconButton from '@/ctComponents/IconButton';
import Modal from '@/ctComponents/Modal';
import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import * as yup from 'yup';

const passwordSchema = yup.object().shape({
  old_password: yup
    .string()
    .min(8, 'Old password must be at least 8 characters')
    .nullable(),
  new_password: yup
    .string()
    .required('New password is required')
    .min(8, 'New password must be at least 8 characters'),
  confirm_password: yup
    .string()
    .required('Please confirm your new password')
    .oneOf([yup.ref('new_password')], 'Passwords must match'),
});

export const ChangePassword = ({ show, setShow }) => {
  const notify = useNotify();

  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    show ? noScroll() : removeNoScroll();
  }, [show]);

  const validateForm = async () => {
    try {
      await passwordSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      const isValid = await validateForm();

      if (!isValid) {
        // notify('Please fix the errors before submitting', 'error');
        SaveApiResMessage({
          title: 'Error',
          message: 'Please fix the errors before submitting',
          mode: 'error',
        });
        setIsSubmitting(false);
        return;
      }

      const passwordData = {
        old_password: formData.old_password || null,
        new_password: formData.new_password,
        new_password_confirmation: formData.confirm_password,
      };

      await changePassword(passwordData);
      // notify('Password changed successfully', 'success');
      SaveApiResMessage({
        title: null,
        message: 'Password changed successfully',
        mode: 'success',
      });

      setFormData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });
      setShow(false);
    } catch (error) {
      // notify(error || 'Failed to change password', 'error');
      SaveApiResMessage({
        title: 'Error',
        message: error || 'Failed to change password',
        mode: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      old_password: '',
      new_password: '',
      confirm_password: '',
    });
    setErrors({});
    setShow(false);
  };

  return (
    <Modal
      noX
      minWidth="unset"
      show={show}
      notClose={true}
      children={
        <div className="py-4 w-full overflow-y-auto space-y-4 max-w-2xl">
          <p className="col-span-2 text-secondary-text text-base">
            If you don’t have a previous password, please leave the ‘Old
            Password’ field blank.
          </p>
          <Input
            className="text-left"
            label="Old Password"
            name="old_password"
            id="old_password"
            placeholder="Enter current password"
            error={errors.old_password}
            value={formData.old_password}
            onChange={(e) => handleChange('old_password', e.target.value)}
            type="password"
          />

          <Input
            className="text-left"
            label="New Password"
            name="new_password"
            id="new_password"
            placeholder="Enter new password"
            error={errors.new_password}
            value={formData.new_password}
            onChange={(e) => handleChange('new_password', e.target.value)}
            type="password"
          />

          <Input
            className="text-left"
            label="Confirm New Password"
            name="confirm_password"
            id="confirm_password"
            placeholder="Confirm new password"
            error={errors.confirm_password}
            value={formData.confirm_password}
            onChange={(e) => handleChange('confirm_password', e.target.value)}
            type="password"
          />

          <div className="flex justify-end gap-4 mt-6 pt-4 border-t">
            <IconButton
              onClick={handleCancel}
              text={'Cancel'}
              color={'#131617'}
              size={'normal'}
              style={{
                minWidth: 120,
                height: 40,
              }}
            />
            <IconButton
              onClick={handleSave}
              text={isSubmitting ? 'Changing...' : 'Change Password'}
              color={'#FFAD00'}
              size={'normal'}
              disabled={isSubmitting}
              style={{
                minWidth: 120,
                backgroundColor: '#FFAD00',
                color: '#131617',
                opacity: isSubmitting ? 0.7 : 1,
                height: 40,
              }}
            />
          </div>
        </div>
      }
      icon={'lock'}
      title="Change Password"
      setShow={setShow}
    />
  );
};
